// The verification view's shared state (TGCLOUD-1868, plan D2, D3, D5, D6, D7): what is selected,
// whether the report could be shown, the telemetry session, and which Test value each place in the
// report is about.
//
//   const view = useVerificationView({ mode, reading, origin, saved, document, readOnly });
//   <VerificationContext.Provider value={view}> … </VerificationContext.Provider>
//   const view = useVerification();          // anywhere inside: null outside a view
//
// THE ARGUMENTS
//   mode         'review' (a report being checked before the Test is saved), 'saved' (a saved
//                Test's report, shown again read-only), or null: no view, and nothing below does
//                anything
//   reading      review: the intake (or hand-off) answer, by PROPOSAL paths. Its `token` starts the
//                telemetry session; nothing here ever decodes it (readingDigest.js says why)
//   origin       review: 'upload' or 'inbox', for the session
//   saved        saved: GET /test-results/:id/anchors' answer, by the record's STABLE paths
//   document     what ReportViewer draws (reportSources.js). A new `document.key` is a new report:
//                the selection, the report's status and the acknowledgement start again
//   readOnly     the view writes nothing: a saved Test's report, or a read-only member
//   nameVariant  saved: (findingId) => "MLH1 c.1852_1854del", to name a variant's boxes in words
//
// A TARGET is one value on the Test form (verificationTargets.js): `field:<name>`, `gene:<KEY>`,
// `variant:<findingId>`. A target the reading has a value for carries a mark (AnchorMark) whether
// or not the form still holds the report's value: a value edited, or typed where the reading was
// not confident, is still checked against where the report prints it. A target the reading has no
// value for has no mark.
//
// WHAT IT GIVES ([view-shell] draws the panes and the marks with it; [view-groups] builds the group
// cards and the footer on it)
//
//   active, mode, readOnly
//   The report pane, handed to ReportViewer as they are:
//     document, anchors, selectedPaths, scrollRequest, onSelectAnchor, onRendered, onRenderFailed
//   reportStatus           'loading' | 'shown' | 'failed'. onRenderFailed is the last word for a
//                          document: a failure after the first page was shown is still a failure.
//                          "Try again" in the pane that then draws it makes it 'shown' again
//   pane, setPane          'test' | 'report': the tab shown below 1200px. Selecting a value shows
//                          the report; selecting a box in the report shows the Test, when the box is
//                          about a value on it (a note on what was not read is about none)
//   select(paths, from)    `from` 'value' (a value's "Show in the report"): the report selects and
//                          scrolls to the first path, and that is all: the value asked from is not
//                          ringed on the Test (Tim, 2026-10-04: the report is where to look).
//                          `from` 'report' (a box was clicked): the Test values those paths are
//                          about are ringed and the Test pane scrolls to the first, so the place it
//                          lands on is plain. Both count one anchor opened
//   showInReport(target, { fields })   select(the target's anchored paths, 'value')
//   highlight(paths)       the view's own pointer: the report selects and scrolls to the paths, and
//                          nothing else moves (no tab, no anchor counted as opened, nothing ringed
//                          on the Test)
//   clearSelection()
//   selectedPaths          the paths drawn strongly in the report
//   highlighted            Set of the targets a box clicked in the report is about, ringed on the
//                          Test pane (highlightSx); isHighlighted(target). Empty after any other
//                          selection
//   focusRequest           { targets, nonce }: the Test pane scrolls to the first of `targets` it
//                          draws (VerificationLayout does it)
//   anchorStateOf(target, { fields })  verificationTargets.anchorStateOf, with the report's status,
//                          and `reasonText` in words: what AnchorMark draws
//   targetOf(path)         a path's target (verificationTargets.targetOfPath), or null
//   index                  the targets the report has values for (verificationTargets.indexReading)
//
//   For the footer and the save ([view-groups]):
//     waitingForReport     a review whose report is still on its way: Save waits for it, since a
//                          report never drawn was never checked against (ReportViewer gives up after
//                          LOAD_TIMEOUT_MS and reports a failure)
//     needsUnverifiableAcknowledgement   a review whose report could not be shown (or that has no
//                          report to draw): the footer asks
//                          "I have checked these values against the report another way"
//                          (labReportVerificationVocabulary.UNVERIFIABLE_ACKNOWLEDGEMENT) and Save
//                          waits for it
//     unverifiableAcknowledged, setUnverifiableAcknowledged(boolean)
//     acknowledgeUnverifiable            what labReportApplication.acknowledgeUnverifiable sends
//     sessionId                          the telemetry session, for labReportApplication.sessionId
//     applicationFields                  { sessionId?, acknowledgeUnverifiable? }: spread into
//                          labReportApplication, so the save sends exactly these two from here
//     counters                           useVerificationSession's: anchorOpened is counted here,
//                          and groupsTotal set from the reading's groups; valueTyped(differed) and
//                          groupConfirmed() are the group cards' to call
//     valueEdited()                      a value still the report's was changed: TestForm calls it
//                          where it clears that value's "from the report" mark
//     finish(outcome)                    'saved' | 'closed' | 'sent-to-inbox': once, before the
//                          review goes. Closing the view without it finishes 'closed'
//     groups, lowConfidence, geneList, notRead, reportKeys   the reading's own lists, as the answer
//                          gives them (review), or the saved record's groups (saved)
//
//   highlightSx(on, { round })   sx for a highlighted value's container (a ring that moves nothing)
//   highlightRowSx(on)           the same for a table row
//   Each value's container says which target it is (`data-verification-target`), so a box selected
//   in the report can scroll the Test pane to it.
import React from 'react';
import { alpha } from '@mui/material/styles';
import useVerificationSession from 'src/hooks/useVerificationSession';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  PATH_STYLES,
  anchorStateOf as stateIn,
  indexReading,
  targetsOfPaths
} from './verificationTargets';
import { unanchoredReasonText } from './labReportVerificationVocabulary';
import { savedAnchorLabel } from './savedAnchorLabels';
import {
  markOtherIndividualRows,
  savedOtherIndividualAnchors
} from './whichIndividual';

export const VerificationContext = React.createContext(null);

/** The verification view this component is inside, or null. */
export const useVerification = () => React.useContext(VerificationContext);

export const VERIFICATION_MODES = Object.freeze(['review', 'saved']);
export const PANES = Object.freeze({ report: 'report', test: 'test' });

const EMPTY = Object.freeze([]);
const NO_TARGETS = Object.freeze(new Set());
const EMPTY_INDEX = Object.freeze({
  byTarget: new Map(),
  targetOf: () => null
});
const listOf = (v) => (Array.isArray(v) ? v : EMPTY);

const fresh = (docKey) => ({
  docKey,
  selectedPaths: EMPTY,
  highlighted: NO_TARGETS,
  scrollRequest: null,
  focusRequest: null,
  pane: PANES.test,
  reportStatus: 'loading',
  reportError: null,
  unverifiableAcknowledged: false
});

function reducer(state, action) {
  const base = state.docKey === action.docKey ? state : fresh(action.docKey);
  switch (action.type) {
    case 'select': {
      const { nonce } = action;
      const fromValue = action.from === 'value';
      return {
        ...base,
        selectedPaths: action.paths,
        highlighted: fromValue ? NO_TARGETS : new Set(action.targets),
        scrollRequest:
          fromValue && action.paths.length
            ? { path: action.paths[0], nonce }
            : base.scrollRequest,
        focusRequest:
          !fromValue && action.targets.length
            ? { targets: action.targets, nonce }
            : base.focusRequest,
        // A box about no value on the Test (a note on what was not read, a report key) keeps the
        // report in view: there is nothing on the Test to show.
        pane: fromValue
          ? PANES.report
          : action.targets.length
          ? PANES.test
          : base.pane
      };
    }
    case 'highlight':
      // The view's own pointer, not the clinician's: the report shows the place, and nothing else
      // moves (no tab, no Test value scrolled to).
      return {
        ...base,
        selectedPaths: action.paths,
        highlighted: NO_TARGETS,
        scrollRequest: action.paths.length
          ? { path: action.paths[0], nonce: action.nonce }
          : base.scrollRequest
      };
    case 'clear':
      return { ...base, selectedPaths: EMPTY, highlighted: NO_TARGETS };
    case 'pane':
      return base.pane === action.pane ? base : { ...base, pane: action.pane };
    case 'rendered':
      return { ...base, reportStatus: 'shown', reportError: null };
    case 'failed':
      return { ...base, reportStatus: 'failed', reportError: action.error };
    case 'acknowledge':
      return { ...base, unverifiableAcknowledged: action.value === true };
    default:
      return base;
  }
}

/**
 * sx for a Test value the selection is about: the theme's information blue, which the report pane
 * draws a selected box with (ReportViewer, COLOURS), as a ring that moves nothing (a shadow, so a
 * gold unsaved-change outline beside it stays).
 */
export const highlightSx = (on, { round = true } = {}) =>
  on
    ? {
        ...(round ? { borderRadius: 1 } : {}),
        boxShadow: (theme) => `0 0 0 2px ${theme.palette.info.main}`
      }
    : {};

/** sx for a highlighted table row: its cells tinted, and the ring inside the row. */
export const highlightRowSx = (on) =>
  on
    ? {
        outline: '2px solid',
        outlineColor: 'info.main',
        outlineOffset: '-2px',
        '& > td': {
          backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08)
        }
      }
    : {};

export function useVerificationView({
  mode = null,
  reading = null,
  origin = 'upload',
  saved = null,
  document = null,
  readOnly = false,
  nameVariant = null
} = {}) {
  // The words each box of the report is named and tagged by (TGCLOUD-1893).
  const t = useAutoTranslation();
  const active = VERIFICATION_MODES.includes(mode);
  const docKey =
    active && document
      ? String(document.key ?? (document.kind === 'blob' ? 'blob' : 'fetch'))
      : null;
  const [stored, dispatch] = React.useReducer(reducer, docKey, fresh);
  // Another report: everything about the last one goes (reducer: a new key starts afresh).
  if (stored.docKey !== docKey) dispatch({ type: 'reset', docKey });
  const state = stored.docKey === docKey ? stored : fresh(docKey);
  // Never reused, across reports too: the report pane honours each scroll request's nonce once.
  const nonceRef = React.useRef(0);

  const review = active && mode === 'review';
  const session = useVerificationSession({
    token: review && reading ? reading.token || null : null,
    origin: origin === 'inbox' ? 'inbox' : 'upload'
  });
  const { counters } = session;

  const groups = React.useMemo(
    () =>
      review
        ? listOf(reading?.verification?.groups)
        : active && mode === 'saved'
        ? listOf(saved?.groups)
        : EMPTY,
    [review, active, mode, reading, saved]
  );
  // How many groups there are to confirm, once the session for this reading has started.
  React.useEffect(() => {
    if (review && reading?.token) counters.setGroupsTotal(groups.length);
  }, [review, reading?.token, groups.length, counters]);

  const index = React.useMemo(() => {
    if (review && reading) {
      return indexReading({
        anchors: reading.anchors,
        unanchored: reading.unanchored,
        fieldSources: reading.fieldSources,
        proposal: reading.proposal,
        pathStyle: PATH_STYLES.proposal,
        withheldGenes: reading.geneList?.withheld
      });
    }
    if (active && mode === 'saved' && saved) {
      return indexReading({
        anchors: saved.anchors,
        unanchored: saved.unanchored,
        pathStyle: PATH_STYLES.stable
      });
    }
    return EMPTY_INDEX;
  }, [review, active, mode, reading, saved]);

  // A saved report's paths are the record's, which the report pane cannot name: each box is given
  // the words for its value (savedAnchorLabels.js). A reading's are named by the pane itself, but
  // for every OTHER person's row of a report about several people, read for one of them
  // (TGCLOUD-1868, Phase E4): drawn apart, and named "Another individual’s result: not this Test".
  // A saved Test's report draws the other people's rows apart the same way, from the anchors
  // answer's `otherIndividuals` (Phase E routine call).
  const anchors = React.useMemo(() => {
    if (review) {
      return markOtherIndividualRows(
        listOf(reading?.anchors),
        listOf(reading?.notRead),
        t
      );
    }
    if (active && mode === 'saved') {
      return [
        ...listOf(saved?.anchors).map((a) =>
          a && typeof a.path === 'string' && !a.label
            ? { ...a, label: savedAnchorLabel(a.path, { nameVariant, t }) }
            : a
        ),
        ...savedOtherIndividualAnchors(saved?.otherIndividuals, t)
      ];
    }
    return EMPTY;
  }, [review, active, mode, reading, saved, nameVariant, t]);

  const act = React.useCallback(
    (action) => dispatch({ ...action, docKey }),
    [docKey]
  );

  const select = React.useCallback(
    (paths, from = 'value') => {
      if (!active) return;
      const list = listOf(paths).filter((p) => typeof p === 'string' && p);
      if (!list.length) return;
      counters.anchorOpened();
      nonceRef.current += 1;
      act({
        type: 'select',
        from: from === 'report' ? 'report' : 'value',
        nonce: nonceRef.current,
        paths: list,
        targets: targetsOfPaths(index, list)
      });
    },
    [active, counters, act, index]
  );

  // A view with no report to draw cannot show one: that is a report that cannot be shown (D5), never
  // one still loading.
  const reportStatus = !active
    ? 'loading'
    : !document
    ? 'failed'
    : state.reportStatus;
  const reportFailed = reportStatus === 'failed';
  const anchorStateOf = React.useCallback(
    (target, { fields = null } = {}) => {
      if (!active) return null;
      const s = stateIn(index, target, { fields, reportFailed });
      if (!s) return null;
      return s.state === 'unanchored'
        ? { ...s, reasonText: unanchoredReasonText(s.reason, t) }
        : s;
    },
    [active, index, reportFailed, t]
  );

  const showInReport = React.useCallback(
    (target, { fields = null } = {}) => {
      const s = active ? stateIn(index, target, { fields }) : null;
      if (s && s.state === 'anchored') select(s.paths, 'value');
    },
    [active, index, select]
  );

  // A place the view itself points the clinician to (the conclusion it could not read with
  // confidence: "Read it (highlighted)"): selected and scrolled to in the report, with no tab
  // switched and no anchor counted as opened, since nobody opened it.
  const highlight = React.useCallback(
    (paths) => {
      if (!active) return;
      const list = listOf(paths).filter((p) => typeof p === 'string' && p);
      if (!list.length) return;
      nonceRef.current += 1;
      act({
        type: 'highlight',
        nonce: nonceRef.current,
        paths: list,
        targets: targetsOfPaths(index, list)
      });
    },
    [active, act, index]
  );

  const needsUnverifiableAcknowledgement = review && !readOnly && reportFailed;
  // Save waits while the report is on its way: a report never drawn was never checked against, so
  // it is neither "shown" nor acknowledged as unverifiable (review of the view; hazard 1868-H7).
  // ReportViewer gives up after LOAD_TIMEOUT_MS, and the acknowledgement is then asked for.
  const waitingForReport = review && !readOnly && reportStatus === 'loading';
  const acknowledgeUnverifiable =
    needsUnverifiableAcknowledgement && state.unverifiableAcknowledged;
  const { sessionId, finish } = session;

  return React.useMemo(
    () => ({
      active,
      mode: active ? mode : null,
      readOnly: active && (readOnly || mode === 'saved'),
      // The report pane
      document: active ? document : null,
      anchors,
      selectedPaths: state.selectedPaths,
      scrollRequest: state.scrollRequest,
      onSelectAnchor: (paths) => select(paths, 'report'),
      onRendered: () => act({ type: 'rendered' }),
      onRenderFailed: (error) => act({ type: 'failed', error: error || null }),
      reportStatus,
      reportError: state.reportError,
      // The tabs below 1200px
      pane: state.pane,
      setPane: (pane) =>
        act({
          type: 'pane',
          pane: pane === PANES.report ? PANES.report : PANES.test
        }),
      // The selection, both ways
      select,
      showInReport,
      highlight,
      clearSelection: () => act({ type: 'clear' }),
      highlighted: state.highlighted,
      isHighlighted: (target) => state.highlighted.has(target),
      focusRequest: state.focusRequest,
      // Where each value is
      index,
      targetOf: index.targetOf,
      anchorStateOf,
      // A report not shown yet, and one that cannot be shown (D5)
      waitingForReport,
      needsUnverifiableAcknowledgement,
      unverifiableAcknowledged: state.unverifiableAcknowledged,
      setUnverifiableAcknowledged: (value) =>
        act({ type: 'acknowledge', value }),
      acknowledgeUnverifiable,
      // Telemetry (D7)
      sessionId: review ? sessionId : null,
      counters,
      valueEdited: () => {
        if (review) counters.valueEdited();
      },
      finish,
      applicationFields: {
        ...(review && sessionId ? { sessionId } : {}),
        ...(acknowledgeUnverifiable ? { acknowledgeUnverifiable: true } : {})
      },
      // The reading's own lists
      groups,
      lowConfidence: review ? listOf(reading?.lowConfidence) : EMPTY,
      geneList: review ? reading?.geneList || null : null,
      notRead: review ? listOf(reading?.notRead) : EMPTY,
      reportKeys: review ? listOf(reading?.reportKeys) : EMPTY
    }),
    [
      active,
      mode,
      readOnly,
      document,
      anchors,
      state,
      select,
      act,
      showInReport,
      highlight,
      index,
      anchorStateOf,
      reportStatus,
      waitingForReport,
      needsUnverifiableAcknowledgement,
      acknowledgeUnverifiable,
      review,
      sessionId,
      counters,
      finish,
      groups,
      reading
    ]
  );
}
